import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SignedIn, SignedOut } from '@clerk/clerk-react';
import { useCart } from '../contexts/CartContext';
import { useMeRole } from '../hooks/useMeRole';
import { useAdmin } from '../hooks/useAdmin';
const EXACT_PATHS = ['/', '/search', '/profile', '/my-purchases', '/my-items', '/account', '/owner', '/admin', '/admin/reports', '/vendor/reports'];
const COMPACT_DELAY_MS = 1000;

export default function BottomNav({ onCloseCart, isCartOpen }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { getTotal } = useCart();
  const { isVendor, isOwner, loading: roleLoading } = useMeRole();
  const { isAdmin, loading: adminLoading } = useAdmin();
  const itemCount = getTotal();
  const isTheme2 = true;

  // After a short delay, collapse to icons-only to save vertical space (helps on admin edit form, etc.)
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setCompact(true), COMPACT_DELAY_MS);
    return () => clearTimeout(t);
  }, [location.pathname]);

  // Admins see Dashboard and Reports in bottom nav. When on admin routes, show admin nav.
  const showAdminNav = isAdmin || location.pathname === '/admin' || location.pathname === '/admin/reports';

  const isActive = (path) => {
    if (EXACT_PATHS.includes(path)) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleNavClick = (path) => {
    if (onCloseCart) onCloseCart();
    navigate(path);
  };

  const handleCartClick = () => {
    if (isCartOpen && onCloseCart) {
      onCloseCart();
    } else {
      window.dispatchEvent(new CustomEvent('openCart'));
    }
  };

  // Admin nav: Dashboard and Reports (bottom nav tabs).
  const adminNavItems = [
    { path: '/admin', label: 'Dashboard', icon: '📊', requireAuth: true },
    { path: '/admin/reports', label: 'Reports', icon: '📈', requireAuth: true },
  ];
  // Owner nav: Dashboard and Profile only (no shopping, cart, or search).
  const ownerNavItems = [
    { path: '/owner', label: 'Dashboard', icon: '📊', requireAuth: true },
    { path: '/profile', label: 'Profile', icon: '👤', requireAuth: true },
  ];
  // Customer nav: Home, Search, Purchases, Account + Cart. Vendor nav: Home, Items, Reports, Profile (no Search, no Cart).
  const customerNavItems = [
    { path: '/', label: 'Home', icon: '🏠' },
    { path: '/search', label: 'Search', icon: '🔍' },
    { path: '/my-purchases', label: 'Purchases', icon: '🛍️', requireAuth: true },
    { path: '/account', label: 'Account', icon: '👤', requireAuth: true },
  ];
  const vendorNavItems = [
    { path: '/', label: 'Home', icon: '🏠' },
    { path: '/my-items', label: 'Items', icon: '📦', requireAuth: true },
    { path: '/vendor/reports', label: 'Reports', icon: '📈', requireAuth: true },
    { path: '/profile', label: 'Profile', icon: '👤', requireAuth: true },
  ];
  const navItems = showAdminNav ? adminNavItems : (roleLoading ? customerNavItems : (isOwner ? ownerNavItems : (isVendor ? vendorNavItems : customerNavItems)));

  const navHeight = compact ? 'h-14' : 'h-20';
  const labelClass = compact
    ? 'absolute opacity-0 w-0 h-0 overflow-hidden pointer-events-none'
    : 'text-xs font-medium truncate w-full';

  return (
    <nav
      className={`fixed bottom-0 left-0 right-0 border-t-2 z-50 safe-area-inset-bottom transition-[height] duration-300 ease-out ${
        isTheme2
          ? 'bg-[#232F3E] border-orange-500/30'
          : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700'
      }`}
      aria-label="Main navigation"
    >
      <div className={`max-w-screen-md mx-auto flex items-center justify-around ${navHeight} px-2 sm:px-4 transition-[height] duration-300 ease-out`}>
        {navItems.map((item) => {
          const btnClass = `flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 h-full rounded-lg transition-colors touch-manipulation ${
            isActive(item.path)
              ? isTheme2
                ? 'bg-orange-500/20 text-orange-400'
                : 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
              : isTheme2
                ? 'text-white/90 hover:bg-white/10'
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
          }`;
          const content = (
            <>
              <span className="text-2xl shrink-0" aria-hidden>{item.icon}</span>
              <span className={labelClass}>{item.label}</span>
            </>
          );
          if (item.requireAuth) {
            return (
              <SignedIn key={item.path}>
                <button type="button" onClick={() => handleNavClick(item.path)} className={btnClass} aria-label={item.label} title={compact ? item.label : undefined}>
                  {content}
                </button>
              </SignedIn>
            );
          }
          return (
            <button key={item.path} type="button" onClick={() => handleNavClick(item.path)} className={btnClass} aria-label={item.label} title={compact ? item.label : undefined}>
              {content}
            </button>
          );
        })}

        {/* Cart button - hidden for owners, admins, and vendors (vendors see Reports instead) */}
        {!isOwner && !showAdminNav && !isVendor && (
          <button
            type="button"
            onClick={handleCartClick}
            className={`flex flex-col items-center justify-center gap-0.5 min-w-0 flex-1 h-full rounded-lg transition-colors touch-manipulation relative ${
              isTheme2 ? 'text-white/90 hover:bg-white/10' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
            aria-label="Shopping cart"
            title={compact ? 'Cart' : undefined}
          >
            <span className="text-2xl relative shrink-0">
              🛒
              {itemCount > 0 && (
                <span className={`absolute -top-1 -right-1 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center border-2 ${isTheme2 ? 'bg-orange-500 border-[#232F3E]' : 'bg-red-500 border-white dark:border-slate-900'}`}>
                  {itemCount > 9 ? '9+' : itemCount}
                </span>
              )}
            </span>
            <span className={labelClass}>Cart</span>
          </button>
        )}
      </div>
    </nav>
  );
}
